import { type ReactNode, Suspense, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';

import { trpc } from '@/lib/trpc';
import { initialRegistry, type Registry, registryStore } from '@/stores/registry';

import Header from './Header';

// Maps the persisted (nullable) row to the frontend store shape, replacing
// nulls with the default empty values declared in `initialRegistry`. Without
// this normalization, controlled inputs would flip from uncontrolled to
// controlled and react-hook-form would warn.
const fieldsFromSaved = (saved: Record<string, unknown>): Partial<Registry> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(saved)) {
    if (value === null) {
      const fallback = (initialRegistry as Record<string, unknown>)[key];

      if (fallback === undefined) {
        continue;
      }

      result[key] = fallback;
      continue;
    }

    result[key] = value;
  }

  return result as Partial<Registry>;
};

const HydrateGate = ({ children }: { children: ReactNode }) => {
  const [{ saved }] = trpc.registry.getStatus.useSuspenseQuery();

  useState(() => {
    if (saved) {
      registryStore.getState().setFields(fieldsFromSaved(saved as Record<string, unknown>));
    }

    return true;
  });

  return <>{children}</>;
};

const LoadingScreen = () => (
  <>
    <Header />
    <section className='flex flex-1 items-center justify-center'>
      <LoadingOutlined className='mb-8! text-5xl!' spin />
    </section>
  </>
);

export const RegistryBootstrap = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<LoadingScreen />}>
    <HydrateGate>{children}</HydrateGate>
  </Suspense>
);
